<template>
  <div>
    <div class="row q-col-gutter-md q-mb-md">
      <span class="text-weight-bold col-12 text-grey-9">
        Select the vehicle model and variations
      </span>
      <div class="col-xs-12 col-md-6">
        <QSelect
          color="secondary"
          v-model="model"
          :options="models"
          outlined
          dense
          clearable
          emit-value
          :disable="isDisabled"
          :display-value="selectedModelObj ? selectedModelObj.name : null"
          option-label="name"
          option-value="id"
          :loading="$apollo.queries.models.loading"
        />
      </div>
      <div class="col-xs-12 col-md-6" v-if="selectedModelObj">
        <QCheckbox
          v-for="transmission in selectedModelObj.transmissionGroups"
          color="secondary"
          :disable="isDisabled"
          :key="transmission.id"
          v-model="selectedTransmissions"
          :val="transmission.id"
          :label="transmission.name"
        />
      </div>
    </div>
    <QSeparator class="bg-grey-4" />

    <div v-if="variants" class="row q-col-gutter-md q-pt-md">
      <VehicleCard
        :card-styles="{
          'q-card--selectable': !isDisabled,
          'q-selected': variant === variantCard.id
        }"
        v-for="variantCard in this.variants"
        :disabled="isDisabled"
        @click="selectVariant"
        :key="variantCard.id"
        :id="variantCard.id"
        :image="variantCard.imageUrl"
        :price="variantCard.listPrice"
        :grossPrice="variantCard.listPriceGross"
        :title="`${variantCard.modelYear} ${selectedModelObj.makeName} ${selectedModelObj.name} ${variantCard.trim}`"
        :description="variantCard.derivative"
        :fuel-type="variantCard.fuelType"
        :transmission="variantCard.transmission"
        :engine="variantCard.engineDescription"
        :driveTrain="variantCard.driveTrain"
        :seats="variantCard.seats"
      />
    </div>

    <QuoteNavigationActions back-to="quoteCarDetails" />
  </div>
</template>

<script>
import { QSelect, QSeparator, QCheckbox, Icons } from 'eclipx.ui';
import { mapState, mapActions } from 'vuex';
import VehicleCard from '../../../components/VehicleCard/VehicleCard';
import QuoteNavigationActions from '../QuoteNavigationActions';
import QueryModels from '../../../graphql/queries/QueryModels';
import { GLOBAL_ERROR } from '../../../components/GlobalError/GlobalError';

export default {
  name: 'CarVariants',
  params: {
    quoteId: String
  },
  data() {
    return {
      model: null,
      selectedTransmissions: [],
      variant: null
    };
  },
  computed: {
    ...mapState({ erpquote: (state) => state.quote.quote.erpquote }),
    selectedModelObj: function () {
      if (!this.models) {
        return null;
      }
      return this.models.find((model) => model.id === this.model);
    },
    selectedVariant: function () {
      return this.variants.find((variant) => variant.id === this.variant);
    },
    variants: function () {
      let variants = [];

      if (this.selectedModelObj) {
        // Show all variants if no transmission is selected
        if (!this.selectedTransmissions.length) {
          return this.selectedModelObj.variants;
        }

        // Convert the selected transmission array into a map
        let transmissionMap = this.selectedTransmissions.reduce(
          (accumulator, currentValue) => ({
            ...accumulator,
            [currentValue]: true
          }),
          {}
        );

        // Filter by variants that have a selected transmission
        this.selectedModelObj.variants.forEach((variant) => {
          if (transmissionMap[variant.transmissionGroupId]) {
            variants.push(variant);
          }
        });
      }

      return variants;
    },
    isDisabled() {
      return Boolean(this.erpquote.erpQuoteRef);
    }
  },
  apollo: {
    models: {
      query: QueryModels,
      variables() {
        const erpquote = this.$store.getters['quote/quote'].erpquote;
        return {
          params: {
            orgId: this.$store.getters['auth/user'].organisation.id,
            productId: erpquote.product.id.toString(),
            makeId: erpquote.make.id,
            bodyTypeId: erpquote.bodyType.id
          }
        };
      },
      skip() {
        return !this.$store.getters['quote/quoteDetailsValid'];
      },
      error: function () {
        this.$euiNotifications.emit({
          notification: GLOBAL_ERROR,
          action: () => {
            return this.$apollo.queries.models.refresh();
          }
        });
      }
    }
  },
  watch: {
    model: function () {
      // Deselect the transmissions when the model changes
      this.selectedTransmissions = [];
    }
  },
  methods: {
    selectVariant(variant) {
      this.variant = variant;
      this.saveCarVariant({
        model: {
          id: this.model,
          name: this.selectedModelObj.name
        },
        variant: { ...this.selectedVariant }
      });
      this.$router.push({
        name: 'quoteLeaseDetails',
        params: { quoteId: this.$route.params.quoteId }
      });
    },
    ...mapActions({ saveCarVariant: 'quote/saveCarVariant' })
  },
  created() {
    // If the prior page is invalid, go to the first incomplete page
    if (!this.$store.getters['quote/quoteDetailsValid']) {
      this.$router.replace({
        name: this.$store.getters['quote/firstIncompletePage'],
        params: { quoteId: this.$route.params.quoteId }
      });
    }

    this.model = this.erpquote.model.id;
    this.variant = this.erpquote.variant.id;

    this.backIcon = Icons.evaArrowBack;
  },
  components: {
    QSelect,
    QSeparator,
    QCheckbox,
    VehicleCard,
    QuoteNavigationActions
  }
};
</script>

<style lang="sass" scoped></style>
